import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { update } from "../../../services/firestoreServices";
import { alert as showAlert } from "../../../utils/alert";
import { File } from "../types";

interface UseFileManagementProps {
  initialFile?: File;
  onFileUpdate?: (file: File) => void;
  onFileDelete?: () => void;
}

export function useFileManagement({
  initialFile,
  onFileUpdate,
  onFileDelete,
}: UseFileManagementProps) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | undefined>(initialFile);
  const [renameDialogVisible, setRenameDialogVisible] = useState(false);
  const [fileName, setFileName] = useState("");
  const [renameError, setRenameError] = useState("");

  useEffect(() => {
    setFile(initialFile);
  }, [initialFile]);

  const openRenameDialog = (targetFile?: File) => {
    const fileToEdit = targetFile || file;
    if (!fileToEdit) return;

    setFile(fileToEdit);

    setFileName(fileToEdit.name);
    setRenameError("");
    setRenameDialogVisible(true);
  };

  const closeRenameDialog = () => {
    setRenameDialogVisible(false);
    setFileName("");
    setRenameError("");
  };

  const openDeleteDialog = (targetFile?: File) => {
    const fileToDelete = targetFile || file;
    if (!fileToDelete) return;

    setFile(fileToDelete);
    showAlert(t("Delete File"), t("Are you sure you want to delete this file?"), [
      {
        text: t("Cancel"),
        style: "cancel",
      },
      {
        text: t("Delete"),
        style: "destructive",
        onPress: () => {
          void deleteFile(fileToDelete);
        },
      },
    ]);
  };

  const handleFileNameChange = (name: string) => {
    setFileName(name);
    setRenameError("");
  };

  const saveRenamedFile = async () => {
    if (!file) return;

    const normalizedName = fileName.trim();

    if (!normalizedName) {
      setRenameError("File name is required");
      return;
    }

    try {
      const now = new Date();

      await update("drive_files", file.id, {
        name: normalizedName,
        updatedAt: now,
      });

      const updatedFile = {
        ...file,
        name: normalizedName,
        updatedAt: now,
      };

      setFile(updatedFile);
      onFileUpdate?.(updatedFile);
      closeRenameDialog();
    } catch (error) {
      console.error("Failed to rename file:", error);
      setRenameError("Failed to rename file. Please try again.");
    }
  };

  const deleteFile = async (targetFile?: File) => {
    const fileToDelete = targetFile || file;
    if (!fileToDelete) return;

    try {
      await update("drive_files", fileToDelete.id, {
        deleted: true,
        updatedAt: new Date(),
      });

      if (onFileDelete) {
        onFileDelete();
      } else {
        setFile(undefined);
      }
    } catch (error) {
      console.error("Failed to delete file:", error);
      showAlert(
        t("Error"),
        t("Failed to delete file. Please try again."),
        [{ text: t("OK") }],
      );
    }
  };

  return {
    file,
    renameDialogVisible,
    fileName,
    renameError,
    openRenameDialog,
    closeRenameDialog,
    openDeleteDialog,
    handleFileNameChange,
    saveRenamedFile,
  };
}
